import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, getSseUrl } from "./api";

function readIceServers() {
  const raw = process.env.REACT_APP_ICE_SERVERS;
  if (!raw) return [{ urls: "stun:stun.l.google.com:19302" }];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [{ urls: "stun:stun.l.google.com:19302" }];
  } catch {
    return [{ urls: "stun:stun.l.google.com:19302" }];
  }
}

const ICE_SERVERS = readIceServers();

function makeClientId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `client-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function stopStream(stream) {
  stream?.getTracks().forEach((track) => track.stop());
}

export function useVideoRoom({ roomId, token, displayName }) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const clientIdRef = useRef(makeClientId());
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteClientIdRef = useRef("");
  const makingOfferRef = useRef(false);
  const ignoreOfferRef = useRef(false);
  const stoppedRef = useRef(false);

  const [status, setStatus] = useState("Starting camera...");
  const [error, setError] = useState("");
  const [participants, setParticipants] = useState([]);
  const [remoteName, setRemoteName] = useState("");
  const [connectionState, setConnectionState] = useState("new");
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [hasRemoteStream, setHasRemoteStream] = useState(false);

  const toggleMic = useCallback(() => {
    const next = !micEnabled;
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = next;
    });
    setMicEnabled(next);
  }, [micEnabled]);

  const toggleCamera = useCallback(() => {
    const next = !cameraEnabled;
    localStreamRef.current?.getVideoTracks().forEach((track) => {
      track.enabled = next;
    });
    setCameraEnabled(next);
  }, [cameraEnabled]);

  useEffect(() => {
    if (!roomId) return undefined;

    if (!token) {
      let cancelled = false;
      const localVideoNode = localVideoRef.current;
      setError("");
      setStatus("Starting demo camera...");
      setHasRemoteStream(false);
      setParticipants([{ clientId: clientIdRef.current, name: displayName || "Demo student", role: "demo" }]);

      const startDemo = async () => {
        try {
          if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error("Camera and microphone require a modern browser on HTTPS or localhost.");
          }
          const localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          if (cancelled) {
            stopStream(localStream);
            return;
          }
          localStreamRef.current = localStream;
          if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
          setMicEnabled(localStream.getAudioTracks().some((track) => track.enabled));
          setCameraEnabled(localStream.getVideoTracks().some((track) => track.enabled));
          setConnectionState("connected");
          setStatus("Demo camera active");
        } catch (startError) {
          setError(startError.message);
          setStatus("Video unavailable");
        }
      };

      startDemo();

      return () => {
        cancelled = true;
        stopStream(localStreamRef.current);
        localStreamRef.current = null;
        if (localVideoNode) localVideoNode.srcObject = null;
      };
    }

    let eventSource;
    stoppedRef.current = false;
    ignoreOfferRef.current = false;
    remoteClientIdRef.current = "";
    const localVideoNode = localVideoRef.current;
    const remoteVideoNode = remoteVideoRef.current;
    setError("");
    setStatus("Starting camera...");
    setHasRemoteStream(false);

    const sendSignal = async (message, to = remoteClientIdRef.current) => {
      if (!to || stoppedRef.current) return;
      try {
        await apiRequest(`/api/video/rooms/${roomId}/signal`, {
          method: "POST",
          token,
          body: {
            clientId: clientIdRef.current,
            to,
            message,
          },
        });
      } catch (requestError) {
        setError(requestError.message);
      }
    };

    const createOffer = async () => {
      const peer = peerRef.current;
      const remoteClientId = remoteClientIdRef.current;
      if (!peer || !remoteClientId || peer.signalingState !== "stable" || makingOfferRef.current) return;

      try {
        makingOfferRef.current = true;
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        await sendSignal({ description: peer.localDescription }, remoteClientId);
      } catch (offerError) {
        setError(offerError.message);
      } finally {
        makingOfferRef.current = false;
      }
    };

    const chooseRemoteParticipant = (roomParticipants = []) => {
      setParticipants(roomParticipants);
      const other = roomParticipants.find((participant) => participant.clientId !== clientIdRef.current);

      if (!other) {
        remoteClientIdRef.current = "";
        setRemoteName("");
        setStatus("Waiting for the other participant...");
        return;
      }

      const hadRemote = Boolean(remoteClientIdRef.current);
      remoteClientIdRef.current = other.clientId;
      setRemoteName(other.name || "Participant");
      setStatus("Connecting video...");

      if (!hadRemote && clientIdRef.current < other.clientId) {
        createOffer();
      }
    };

    const handleSignal = async ({ from, fromName, message }) => {
      const peer = peerRef.current;
      if (!peer || !message || stoppedRef.current) return;

      if (!remoteClientIdRef.current) {
        remoteClientIdRef.current = from;
        setRemoteName(fromName || "Participant");
      }

      try {
        if (message.description) {
          const description = message.description;
          const readyForOffer = !makingOfferRef.current && (peer.signalingState === "stable" || description.type === "answer");
          const offerCollision = description.type === "offer" && !readyForOffer;
          const polite = clientIdRef.current > from;

          ignoreOfferRef.current = !polite && offerCollision;
          if (ignoreOfferRef.current) return;

          await peer.setRemoteDescription(description);
          if (description.type === "offer") {
            const answer = await peer.createAnswer();
            await peer.setLocalDescription(answer);
            await sendSignal({ description: peer.localDescription }, from);
          }
        } else if (message.candidate) {
          try {
            await peer.addIceCandidate(message.candidate);
          } catch (candidateError) {
            if (!ignoreOfferRef.current) throw candidateError;
          }
        }
      } catch (signalError) {
        setError(signalError.message);
      }
    };

    const start = async () => {
      try {
        await apiRequest(`/api/video/rooms/${roomId}/join`, { method: "POST", token });

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera and microphone require a modern browser on HTTPS or localhost.");
        }

        const localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (stoppedRef.current) {
          stopStream(localStream);
          return;
        }

        localStreamRef.current = localStream;
        if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
        setMicEnabled(localStream.getAudioTracks().some((track) => track.enabled));
        setCameraEnabled(localStream.getVideoTracks().some((track) => track.enabled));

        const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        peerRef.current = peer;

        localStream.getTracks().forEach((track) => peer.addTrack(track, localStream));

        peer.onicecandidate = (event) => {
          if (event.candidate) sendSignal({ candidate: event.candidate });
        };
        peer.onconnectionstatechange = () => {
          setConnectionState(peer.connectionState);
          if (peer.connectionState === "connected") setStatus("Connected");
          if (["failed", "disconnected"].includes(peer.connectionState)) setStatus("Connection interrupted");
        };
        peer.ontrack = (event) => {
          const [remoteStream] = event.streams;
          if (remoteStream && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            setHasRemoteStream(true);
            setStatus("Connected");
          }
        };
        peer.onnegotiationneeded = () => {
          if (remoteClientIdRef.current) createOffer();
        };

        const sseUrl = getSseUrl(`/api/video/rooms/${roomId}/events`, {
          token,
          clientId: clientIdRef.current,
          name: displayName,
        });
        eventSource = new EventSource(sseUrl);

        eventSource.addEventListener("room-state", (event) => {
          chooseRemoteParticipant(JSON.parse(event.data).participants || []);
        });
        eventSource.addEventListener("participant-joined", (event) => {
          chooseRemoteParticipant(JSON.parse(event.data).room?.participants || []);
        });
        eventSource.addEventListener("participant-left", (event) => {
          chooseRemoteParticipant(JSON.parse(event.data).room?.participants || []);
          if (!remoteClientIdRef.current) {
            if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
            setHasRemoteStream(false);
          }
        });
        eventSource.addEventListener("signal", (event) => {
          handleSignal(JSON.parse(event.data));
        });
        eventSource.onerror = () => {
          if (!stoppedRef.current) setStatus("Reconnecting signaling...");
        };
      } catch (startError) {
        setError(startError.message);
        setStatus("Video unavailable");
      }
    };

    start();

    return () => {
      stoppedRef.current = true;
      if (eventSource) eventSource.close();
      if (peerRef.current) {
        peerRef.current.onicecandidate = null;
        peerRef.current.ontrack = null;
        peerRef.current.onconnectionstatechange = null;
        peerRef.current.close();
        peerRef.current = null;
      }
      stopStream(localStreamRef.current);
      localStreamRef.current = null;
      if (localVideoNode) localVideoNode.srcObject = null;
      if (remoteVideoNode) remoteVideoNode.srcObject = null;
    };
  }, [displayName, roomId, token]);

  return {
    localVideoRef,
    remoteVideoRef,
    status,
    error,
    participants,
    remoteName,
    connectionState,
    micEnabled,
    cameraEnabled,
    hasRemoteStream,
    toggleMic,
    toggleCamera,
  };
}
